import { APP_CONFIG } from "../../../constants/constants";
import i18n from "../../../services/i18n";
import { ColorRGB } from "../../../types/common";
import {
  TGeozoneMapData,
  TGeozoneMapDataRoute,
  TLatLng,
} from "../../../types/map";
import { basicDebounce } from "../../../utils/common";
import { clearCorridor, renderCorridor, renderRoute } from "../../../utils/map";
import { mapInfoBoxContent } from "../../map-fleet/common";
import { TAssetLocationDataPoint } from "../../map-fleet/type";
import { TMapOperationsProps, TMapUpdatesHandler } from "./type";

const dragLabelCenter = {
  // subTitle: "to reposition",
  // title: "Drag",
};
const dragLabelPoints = {
  // subTitle: "to reshape",
  // title: "Drag",
};

export const mapOperations = ({
  drawShapeOnly = false,
  ...props
}: TMapOperationsProps) => {
  const google = (window as any).google;
  (window as any).map = props.mapRef.current;
  (window as any).geozoneEditable = props.mapData.editable;

  // create a namespace for map objects
  props.mapRef.current.objects = {
    mPushpins: {
      pCentre: null,
      rPoints: [] as {
        pushpin: any;
        circle: any;
        circleLayer: any;
        rectLayer?: any;
        rect?: any;
      }[],
    },
    mRoutes: [],
    mCorridorElements: [] as any[],
    mInfobox: null,
    circleRadius:
      (props.mapData as TGeozoneMapDataRoute).locs ??
      APP_CONFIG.MAPS.DEFAULTS.RADIUS,
  };

  const renderMapObjects = async () => {
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");
    const color = (props.mapData.color ??
      APP_CONFIG.MAPS.DEFAULTS.ZONE_COLOR) as ColorRGB;
    let refCenter: google.maps.LatLng = new google.maps.LatLng(
      (props.mapData as TGeozoneMapDataRoute).centerPosition.latitude,
      (props.mapData as TGeozoneMapDataRoute).centerPosition.longitude
    );
    if (APP_CONFIG.DEBUG.GEOZONES)
      console.log("Dropping center at", refCenter.lat(), refCenter.lng());
    if (!drawShapeOnly)
      props.mapRef.current.map.setOptions({ center: refCenter }); // set the view to the center

    const corridorRadius =
      (props.mapData as TGeozoneMapDataRoute).radius ??
      APP_CONFIG.MAPS.DEFAULTS.RADIUS;

    // TODO: uncertain if routes can have a centre pushpin
    // create center pushpin
    // props.mapRef.current.objects.mPushpins.pCentre = new Microsoft.Maps.Pushpin(
    //   refCenter,
    //   {
    //     anchor: new Microsoft.Maps.Point(16, 32),
    //     color: `rgba(${color.join(",")},0.2)`,
    //     // icon: MapMarkerRed,
    //     visible: props.mapData.editable,
    //     draggable: props.mapData.editable,
    //     ...(props.mapData.editable ? dragLabelCenter : {}),
    //   }
    // );

    // get the route points
    let routePoints: google.maps.LatLng[] = !!(
      props.mapData as TGeozoneMapDataRoute
    ).locs
      ? (props.mapData as TGeozoneMapDataRoute).locs.map(
          (point: TLatLng) =>
            new google.maps.LatLng(point.latitude, point.longitude)
        )
      : [];
    if (APP_CONFIG.DEBUG.GEOZONES) console.log("routePoints", routePoints);

    // create pushpins for the route points and draw the corridor
    routePoints.forEach((routePoint: google.maps.LatLng, index: number) => {
      // const newPushpin = new Microsoft.Maps.Pushpin(routePoint, {
      //   anchor: new Microsoft.Maps.Point(16, 32),
      //   icon: svgIcons.MapMarkerRed,
      //   visible: props.mapData.editable,
      //   draggable: props.mapData.editable,
      //   ...(props.mapData.editable ? dragLabelPoints : {}),
      // });
      // props.mapRef.current.map.entities.push(newPushpin);
      const newPushpin = new AdvancedMarkerElement({
        position: routePoint,
        map: props.mapData.editable ? props.mapRef.current.map : null,
        gmpDraggable: true, // props.mapData.editable,
        ...(props.mapData.editable ? dragLabelCenter : {}),
      });

      // create corridor for the new location
      const routeCorridorData = renderCorridor(
        props.mapRef,
        routePoint,
        corridorRadius,
        color,
        !!routePoints[index - 1]
          ? new google.maps.LatLng(
              routePoints[index - 1].lat(),
              routePoints[index - 1].lng()
            )
          : undefined
      );
      props.mapRef.current.objects.mPushpins.rPoints.push({
        pushpin: newPushpin,
        ...routeCorridorData,
      });
    });

    // render the polygon
    renderRoute(props.mapRef, refCenter, routePoints, color, drawShapeOnly);
    // add an infobox to the center of the shape, that shows on hover
    if (drawShapeOnly && !!props.geozoneId) {
      const bounds = new google.maps.LatLngBounds();
      props.mapRef.current.objects.mPushpins.rPoints.forEach(
        (dataPoint: any) => {
          bounds.extend({
            lat: dataPoint.pushpin.position.lat,
            lng: dataPoint.pushpin.position.lng,
          });
        }
      );
      const infobox = new google.maps.InfoWindow({
        content: mapInfoBoxContent(
          { deviceid: props.geozoneId } as TAssetLocationDataPoint,
          true
        ),
        position: bounds.getCenter(), // used to identify position of the infobox
        headerDisabled: true,
        zIndex: APP_CONFIG.MAPS.INFOBOX_ZINDEX,
      });
      const mouseoverHandler = (e: any) => {
        const currentZoom = props.mapRef.current.map.getZoom();
        infobox.setOptions({
          disableAutoPan: currentZoom >= APP_CONFIG.MAPS.ZOOM_MAX - 2,
        });
        infobox.setPosition(
          props.mapRef.current.map.getBounds().contains(bounds.getCenter()) &&
            currentZoom < APP_CONFIG.MAPS.ZOOM_MAX - 2
            ? bounds.getCenter() // center of the geozone bounds
            : true
              ? new google.maps.LatLng(e.latLng.lat(), e.latLng.lng()) // position of the cursor
              : props.mapRef.current.map.getCenter() // map viewport center
        );
        infobox.open({
          // anchor: circleLayer,
          map: props.mapRef.current.map,
          shouldFocus: null,
        });
        // Close info box after 4 sec to let user interact with other markers
        setTimeout(() => {
          infobox.close();
        }, 4000);
      };
      const debouncedMouseoverHandler = basicDebounce(
        mouseoverHandler,
        1000 * 1
      );
      // infobox.setMap(props.mapRef.current.map);
      props.mapRef.current.objects.mCorridorElements.forEach(
        (corridorElement: any) => {
          google.maps.event.addListener(
            corridorElement,
            "mouseover",
            debouncedMouseoverHandler
          );

          google.maps.event.addListener(
            corridorElement,
            "mouseout",
            (e: any) => {
              infobox.close();
            }
          );
        }
      );
    }
    if (drawShapeOnly) return;

    // event listener to add a route point when user clicks on the map
    props.mapRef.current.map.addListener("click", function (e: any) {
      const thisProps = (window as any).propsForGeozoneMapEvents || props;
      const editable =
        (window as any).geozoneEditable || thisProps.mapData.editable || false;
      if (APP_CONFIG.DEBUG.GEOZONES)
        console.log(
          (window as any).geozoneEditable,
          thisProps.mapData.editable
        );

      if (
        editable === false ||
        routePoints.length >= APP_CONFIG.MAPS.DEFAULTS.ROUTE_POINTS_MAX
      )
        return; // do nothing if not editable or max points reached

      // Get the location of the click event
      const newLocation = e.latLng;

      // Add the location to the routePoints array
      routePoints.push(newLocation);

      // use the route points to create pushpins
      // const newPushpin = new Microsoft.Maps.Pushpin(newLocation, {
      //   anchor: new Microsoft.Maps.Point(16, 32),
      //   icon: svgIcons.MapMarkerRed,
      //   visible: editable,
      //   draggable: editable,
      //   ...(editable ? dragLabelPoints : {}),
      // });
      // thisProps.mapRef.current.map.entities.push(newPushpin);
      const newPushpin = new AdvancedMarkerElement({
        position: newLocation,
        map: props.mapData.editable ? props.mapRef.current.map : null,
        gmpDraggable: true, // props.mapData.editable,
        ...(props.mapData.editable ? dragLabelCenter : {}),
      });

      // dragend event for the new pushpin
      google.maps.event.addListener(newPushpin, "dragend", (args: any) => {
        const thisProps = (window as any).propsForGeozoneMapEvents || props;
        // TODO: DO WE REALLY NEED TO REDRAW THE ROUTE?
        // --------------------------------------------
        // redraw the route
        const routePointLocations =
          thisProps.mapRef.current.objects.mPushpins.rPoints.map(
            (rPoint: any) => rPoint.pushpin.position
          );
        routePoints = routePointLocations;
        if (APP_CONFIG.DEBUG.GEOZONES)
          console.log("routePointLocations", routePointLocations);
        // Draw the new route
        renderRoute(thisProps.mapRef, refCenter, routePointLocations, color);

        // remove corridor shapes
        // props.mapRef.current.objects.mPushpins.rPoints.forEach(
        //   (rPoint: any) => {
        //     if (rPoint.circleLayer)
        //       props.mapRef.current.map.layers.remove(rPoint.circleLayer);
        //     if (rPoint.rectLayer)
        //       props.mapRef.current.map.layers.remove(rPoint.rectLayer);
        //   }
        // );
        clearCorridor(props.mapRef);

        // create corridor for all locations again but keep pushpins
        props.mapRef.current.objects.mPushpins.rPoints.forEach(
          (rPoint: any, index: number) => {
            const routeCorridorData = renderCorridor(
              props.mapRef,
              rPoint.pushpin.position,
              corridorRadius,
              color,
              routePoints[index - 1]
            );
            rPoint.circle = routeCorridorData.circle;
            rPoint.circleLayer = routeCorridorData.circleLayer;
            if (index === 0) return; // skip the first point (center)
            rPoint.rectLayer = routeCorridorData.rectLayer;
            rPoint.rect = routeCorridorData.rect;
          }
        );

        // update the map data
        if (APP_CONFIG.DEBUG.GEOZONES)
          console.log("[setMapData] via mapOperations (radius dragend)");
      });

      // create corridor for the new location
      const routeCorridorData = renderCorridor(
        props.mapRef,
        newLocation,
        corridorRadius,
        color,
        routePoints[routePoints.length - 2]
      );

      // update the map data
      props.mapRef.current.objects.mPushpins.rPoints.push({
        pushpin: newPushpin,
        ...routeCorridorData,
      });
      const routePointLocations =
        props.mapRef.current.objects.mPushpins.rPoints.map(
          (rPoint: any) => rPoint.pushpin.position
        );
      props.setMapData({
        ...props.mapData,
        centerPosition: {
          latitude: refCenter.lat(),
          longitude: refCenter.lng(),
        },
        locs: routePointLocations.map(
          (point: any) =>
            ({
              latitude: point.lat,
              longitude: point.lng,
            }) as TLatLng
        ),
      } as TGeozoneMapData);
      renderRoute(props.mapRef, refCenter, routePoints, color, true);
    });

    // dragend event for existing pushpins
    props.mapRef.current.objects.mPushpins.rPoints.map((rPoint: any) => {
      google.maps.event.addListener(rPoint.pushpin, "dragend", (args: any) => {
        // redraw the route
        const routePointLocations =
          props.mapRef.current.objects.mPushpins.rPoints.map(
            (rPoint: any) => rPoint.pushpin.position
          );
        routePoints = routePointLocations;
        if (APP_CONFIG.DEBUG.GEOZONES)
          console.log("routePointLocations", routePointLocations);

        // remove corridor shapes
        // props.mapRef.current.objects.mPushpins.rPoints.forEach(
        //   (rPoint: any) => {
        //     if (rPoint.circleLayer)
        //       props.mapRef.current.map.layers.remove(rPoint.circleLayer);
        //     if (rPoint.rectLayer)
        //       props.mapRef.current.map.layers.remove(rPoint.rectLayer);
        //   }
        // );
        clearCorridor(props.mapRef);

        // create corridor for all locations again but keep pushpins
        props.mapRef.current.objects.mPushpins.rPoints.forEach(
          (rPoint: any, index: number) => {
            const routeCorridorData = renderCorridor(
              props.mapRef,
              rPoint.pushpin.position,
              corridorRadius,
              color,
              routePoints[index - 1]
            );
            rPoint.circle = routeCorridorData.circle;
            rPoint.circleLayer = routeCorridorData.circleLayer;
            if (index === 0) return; // skip the first point (center)
            rPoint.rectLayer = routeCorridorData.rectLayer;
            rPoint.rect = routeCorridorData.rect;
            // TODO: need to fix dragging of last point
          }
        );

        // Draw the new route
        renderRoute(props.mapRef, refCenter, routePointLocations, color);
        // update the map data
        if (APP_CONFIG.DEBUG.GEOZONES)
          console.log("[setMapData] via mapOperations (radius dragend)");
        props.setMapData({
          ...props.mapData,
          centerPosition: {
            latitude: refCenter.lat(),
            longitude: refCenter.lng(),
          },
          locs: routePointLocations.map(
            (point: any) =>
              ({
                latitude: point.lat,
                longitude: point.lng,
              }) as TLatLng
          ),
        } as TGeozoneMapData);
      });
    });
  };

  // load map modules before rendering the map objects
  renderMapObjects();
};

export const mapUpdatesHandler: TMapUpdatesHandler = (props, action, value) => {
  if (APP_CONFIG.DEBUG.GEOZONES)
    console.log("[mapUpdatesHandler]", action, value);

  const Microsoft = (window as any).Microsoft;
  const refCenter = props.mapRef.current.map.getCenter();

  switch (action) {
    case "edit":
      // set global variables for map events to identify if the map is in editable mode
      (window as any).propsForGeozoneMapEvents = props;
      (window as any).geozoneEditable = value;

      // edge pushpins are draggable
      props.mapRef.current.objects.mPushpins?.rPoints?.forEach(
        (rPoint: any) => {
          // rPoint.pushpin.setOptions({
          //   visible: value,
          //   draggable: value,
          //   // ...(value ? dragLabelPoints : {})
          // });
          rPoint.pushpin.setMap(props.mapRef.current.map);
        }
      );
      // show the infobox
      if (false) {
        props.mapRef.current.objects.mInfobox = new Microsoft.Maps.Infobox(
          refCenter,
          {
            title: i18n.t("admins.geozones.detailsPage.map.routeEdit.heading"),
            description: i18n.t(
              "admins.geozones.detailsPage.map.routeEdit.sub_heading"
            ),
            showPointer: false,
            // showCloseButton: false,
            visible: false,
            actions: [
              {
                label: i18n.t(
                  "admins.geozones.detailsPage.map.routeEdit.ok_button"
                ),
                eventHandler: () => {
                  // close info box
                  props.mapRef.current.objects.mInfobox.setOptions({
                    visible: false,
                  });
                },
              },
            ],
          }
        );
        props.mapRef.current.objects.mInfobox.setMap(props.mapRef.current.map);
        props.mapRef.current.objects.mInfobox.setOptions({ visible: true });
        setTimeout(() => {
          const infoboxElement = (window as any).document.querySelector(
            ".MicrosoftMap .Infobox"
          )?.parentNode;
          if (infoboxElement) {
            (infoboxElement as any).style.transition = "all 0.5s ease 0s";
            (infoboxElement as any).style.left = "20px";
            (infoboxElement as any).style.top = "20px";
          }
        }, 100);
      }
      break;
    default:
      break;
  }
};
